import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useRealtime } from "../lib/realtime";
import { ApprovalCard } from "../pages/Visitors";

/* Global, MyGate-style resident popup. When a VISITOR_APPROVAL notification arrives
   (or a pending visitor exists on load), surface a prominent Approve/Deny modal.
   Mounted only for residents. */
export default function VisitorApprovalListener() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [queue, setQueue] = useState([]); // pending visitors awaiting this resident
  const isResident = user?.role === "OWNER" || user?.role === "TENANT";

  const pull = useCallback(() => {
    if (!isResident) return;
    api.get("/api/visitors/pending").then(({ data }) => setQueue(data.items || [])).catch(() => {});
  }, [isResident]);

  useEffect(() => { pull(); }, [pull]);

  useEffect(() => {
    if (!isResident) return;
    return subscribe((m) => {
      if (m.type === "notification" && m.data?.kind === "VISITOR_APPROVAL") pull();
    });
  }, [subscribe, isResident, pull]);

  if (!isResident || queue.length === 0) return null;
  const v = queue[0];

  function done() {
    setQueue((q) => q.filter((x) => x.id !== v.id));
  }

  return (
    <AnimatePresence>
      <motion.div key="vis-overlay" className="fixed inset-0 z-[120] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <motion.div initial={{ scale: 0.94, y: 16, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
          className="relative w-full max-w-md" data-testid="visitor-popup">
          <div className="mb-3 flex items-center justify-center gap-2 text-neon-cyan">
            <ShieldCheck size={18} weight="fill" />
            <span className="text-[12px] font-bold uppercase tracking-[0.25em]">Visitor at your gate</span>
          </div>
          <ApprovalCard v={v} onDone={done} />
          {queue.length > 1 && <p className="mt-2 text-center text-[11px] text-white/45">{queue.length - 1} more waiting</p>}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
